import { useEffect } from "react";
import TextInput from "uicomponents/TextInput";
import TextArea from "uicomponents/TextArea";
import RadioInput from "uicomponents/RadioInput";
import { useFormikContext } from "formik";
import { CensusFormType } from "pages/WorkersCompApplcation/utils/formType";
import { sectionsQuestions } from "pages/WorkersCompApplcation/utils/sectionsQuestions";
import SelectInput from "uicomponents/SelectInput";
import { EmployeeOfficeList } from "types";
import { getOfficeFullAddress } from "utils/helper";
import { SystemIcon, Tooltip } from "@justworkshr/milo-components";
import styles from "../OwnerAndEmployeeCensus.module.css";
import {
  employeeTypeOptions,
  stateOption,
} from "pages/WorkersCompApplcation/utils/selectInputOptions";
import { getMemberUuid } from "utils/idHelpers";

interface ICensusComponentProps {
  officeLocations?: EmployeeOfficeList;
  isSavedToCensus: boolean;
  fieldRefs?: React.MutableRefObject<{
    [key: string]: HTMLInputElement | null;
  }>;
}

const CensusComponent = ({ ...props }: ICensusComponentProps): JSX.Element => {
  const { officeLocations, isSavedToCensus, fieldRefs } = props;
  const { values, setFieldValue } = useFormikContext<CensusFormType>();

  const isRemote = values?.isRemote;
  const hasSalary = values?.isPaid;
  const salaryType = values?.salaryType;
  const isOwner = values?.censusType === "Owner";
  const isEmployee = values?.censusType === "Employee";
  const isFirstUser = values.uuid === getMemberUuid();

  const labelList = sectionsQuestions["census"].questions;
  const firstUserLabelList = sectionsQuestions["census"].firstUserQuestions;
  const messageList = sectionsQuestions["census"].message;

  const officeLocationOptions = officeLocations?.map((address: any) => ({
    value: address.uuid,
    description: address ? getOfficeFullAddress(address) : "",
  }));

  useEffect(() => {
    if (isFirstUser) {
      setFieldValue("adminAccess", "Yes");
    }
  }, [isFirstUser, setFieldValue]);

  useEffect(() => {
    if (hasSalary === "Unpaid") {
      const updateFieldList = [
        "salary",
        "salaryType",
        "hourlyRate",
        "hoursWeekly",
        "exemptionStatus",
      ];
      updateFieldList.forEach((field) => {
        setFieldValue(field, "");
      });
    }
  }, [hasSalary, setFieldValue]);

  useEffect(() => {
    if (salaryType === "Annual salary") {
      setFieldValue("hourlyRate", "");
      setFieldValue("hoursWeekly", "");
    } else {
      setFieldValue("salary", "");
    }
  }, [salaryType, setFieldValue]);

  useEffect(() => {
    if (isRemote === "No") {
      setFieldValue("address1", "");
      setFieldValue("address2", "");
      setFieldValue("city", "");
      setFieldValue("state", "");
      setFieldValue("zip", "");
    } else {
      setFieldValue("officeLocationId", "");
    }
  }, [isRemote, setFieldValue]);

  useEffect(() => {
    if (isOwner) {
      const updateFieldList = ["adminAccess", "employeeType"];
      updateFieldList.forEach((field) => {
        setFieldValue(field, "");
      });
    }
  }, [isOwner, setFieldValue]);

  useEffect(() => {
    if (isEmployee) {
      const updateFieldList = ["isPaid", "ownerStake"];
      updateFieldList.forEach((field) => {
        setFieldValue(field, "");
      });
    }
  }, [isEmployee, setFieldValue]);
  return (
    <>
      <div>
        <TextInput
          name="firstName"
          type="string"
          label={labelList.firstName}
          fieldRefs={fieldRefs}
        />
        <TextInput
          name="middleName"
          type="string"
          label={labelList.middleName}
          required={false}
        />
        <TextInput
          name="lastName"
          type="string"
          label={labelList.lastName}
          fieldRefs={fieldRefs}
        />
        <RadioInput
          name="isRemote"
          label={isFirstUser ? firstUserLabelList.isRemote : labelList.isRemote}
          options={["Yes", "No"]}
          fieldRefs={fieldRefs}
        />
        {isRemote === "Yes" && (
          <>
            <div className={styles.subtitle}>Enter remote location</div>
            <div className={styles.remoteFields}>
              <TextInput
                name="address1"
                type="string"
                label={labelList.address1}
                message={messageList.address1}
                fieldRefs={fieldRefs}
              />
              <TextInput
                name="address2"
                type="string"
                label={labelList.address2}
                required={false}
              />
              <TextInput
                name="city"
                type="string"
                label={labelList.city}
                fieldRefs={fieldRefs}
              />
              <SelectInput
                placeholder="Select an option"
                name="state"
                label={labelList.state}
                options={stateOption}
                fieldRefs={fieldRefs}
              />
              <TextInput
                name="zip"
                type="string"
                label={labelList.zip}
                fieldRefs={fieldRefs}
              />
            </div>
          </>
        )}
        {isRemote === "No" && (
          <SelectInput
            name="officeLocationId"
            options={officeLocationOptions}
            label={labelList.officeLocationId}
            placeholder="Select an option"
            fieldRefs={fieldRefs}
          />
        )}
        <RadioInput
          name="censusType"
          label={
            isFirstUser ? firstUserLabelList.censusType : labelList.censusType
          }
          options={["Employee", "Owner"]}
          disabled={isFirstUser || isSavedToCensus}
          fieldRefs={fieldRefs}
        />
        {isOwner && (
          <p className={styles.ownerBlock}>
            <strong>Heads up:</strong> All owners will be listed on the policy,
            even if they’re unpaid.{" "}
          </p>
        )}
        {isOwner && (
          <>
            <TextInput
              name="title"
              type="string"
              label={labelList.title}
              fieldRefs={fieldRefs}
            />
            <TextArea
              name="roleDescription"
              label={labelList.roleDescription}
              fieldRefs={fieldRefs}
            />
            <TextInput
              name="email"
              type="string"
              message={isFirstUser ? messageList.firstOwnerEmail : undefined}
              label={labelList.email}
              disabled={isFirstUser}
              fieldRefs={fieldRefs}
            />
            <TextInput
              name="ownerStake"
              type="string"
              label={labelList.ownerStake}
              fieldRefs={fieldRefs}
            />
            <RadioInput
              name="isPaid"
              label={labelList.isPaid}
              options={["Paid", "Unpaid"]}
              fieldRefs={fieldRefs}
            />
            {hasSalary === "Paid" && (
              <>
                <RadioInput
                  name="salaryType"
                  label={
                    isFirstUser
                      ? firstUserLabelList.salaryType
                      : labelList.salaryType
                  }
                  options={["Annual salary", "Hourly rate"]}
                  fieldRefs={fieldRefs}
                />
                {salaryType === "Annual salary" && (
                  <TextInput
                    name="salary"
                    type="string"
                    label={
                      isFirstUser ? firstUserLabelList.salary : labelList.salary
                    }
                    fieldRefs={fieldRefs}
                  />
                )}
                {salaryType === "Hourly rate" && (
                  <>
                    <TextInput
                      name="hourlyRate"
                      type="string"
                      label={
                        isFirstUser
                          ? firstUserLabelList.hourlyRate
                          : labelList.hourlyRate
                      }
                      fieldRefs={fieldRefs}
                    />
                    <TextInput
                      name="hoursWeekly"
                      type="string"
                      label={
                        isFirstUser
                          ? firstUserLabelList.hoursWeekly
                          : labelList.hoursWeekly
                      }
                      fieldRefs={fieldRefs}
                    />
                  </>
                )}
                <RadioInput
                  name="exemptionStatus"
                  label={labelList.exemptionStatus}
                  options={["Exempt", "Non-exempt"]}
                  fieldRefs={fieldRefs}
                />
              </>
            )}
          </>
        )}

        {isEmployee && (
          <>
            <TextInput
              name="title"
              type="string"
              label={labelList.title}
              fieldRefs={fieldRefs}
            />
            <TextArea
              name="roleDescription"
              label={labelList.roleDescription}
              fieldRefs={fieldRefs}
            />
            <TextInput
              name="email"
              type="string"
              label={labelList.email}
              disabled={isFirstUser}
              message={isFirstUser ? messageList.firstEmployeeEmail : undefined}
              fieldRefs={fieldRefs}
            />
            <RadioInput
              name="exemptionStatus"
              label={labelList.exemptionStatus}
              options={["Exempt", "Non-exempt"]}
              fieldRefs={fieldRefs}
            />
            <SelectInput
              name="employeeType"
              options={employeeTypeOptions}
              label={labelList.employeeType}
              placeholder="Select an option"
              fieldRefs={fieldRefs}
            />
            <RadioInput
              name="salaryType"
              label={
                isFirstUser
                  ? firstUserLabelList.salaryType
                  : labelList.salaryType
              }
              options={["Annual salary", "Hourly rate"]}
              fieldRefs={fieldRefs}
            />
            {salaryType === "Annual salary" && (
              <TextInput
                name="salary"
                type="string"
                label={
                  isFirstUser ? firstUserLabelList.salary : labelList.salary
                }
                fieldRefs={fieldRefs}
              />
            )}
            {salaryType === "Hourly rate" && (
              <>
                <TextInput
                  name="hourlyRate"
                  type="string"
                  label={
                    isFirstUser
                      ? firstUserLabelList.hourlyRate
                      : labelList.hourlyRate
                  }
                  fieldRefs={fieldRefs}
                />
                <TextInput
                  name="hoursWeekly"
                  type="string"
                  label={
                    isFirstUser
                      ? firstUserLabelList.hoursWeekly
                      : labelList.hoursWeekly
                  }
                  fieldRefs={fieldRefs}
                />
              </>
            )}
            <div className={styles.adminAccessParentDiv}>
              <RadioInput
                name="adminAccess"
                label={
                  isFirstUser
                    ? firstUserLabelList.adminAccess
                    : labelList.adminAccess
                }
                options={["Yes", "No"]}
                disabled={isFirstUser}
                message={
                  isFirstUser ? messageList.firstUserAdminAccess : undefined
                }
                fieldRefs={fieldRefs}
              />
              <Tooltip
                title="Admins are only allowed to access the dashboard. All people added as an admin will be able to help complete the application."
                position="top"
                color="neutral"
                data-testid="adminAccess-tooltip-label"
              >
                <SystemIcon iconName="info-circle" size="extra-small" />
              </Tooltip>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default CensusComponent;
